import { useMemo } from 'react';

import type { TPermission } from '@/constants/permissions';

import { useStaffSession } from '@/hooks/auth';
import { hasPermission } from '@/utils/access';

import type { TPersona } from 'constants/navigation';

type TAccess = {
  persona: TPersona | null;
  isReady: boolean;
  can: (permission: TPermission) => boolean;
  canAny: (permissions: readonly TPermission[]) => boolean;
  canAll: (permissions: readonly TPermission[]) => boolean;
};

const useAccess = (): TAccess => {
  const { data } = useStaffSession();

  const isReady = data?.stage === 'ready';
  const persona = isReady ? data?.subRole ?? null : null;

  return useMemo(() => ({
    persona,
    isReady,
    can: (permission: TPermission) => hasPermission(persona, permission),
    canAny: (permissions: readonly TPermission[]) => permissions
      .some(permission => hasPermission(persona, permission)),
    canAll: (permissions: readonly TPermission[]) => permissions
      .every(permission => hasPermission(persona, permission)),
  }), [persona, isReady]);
};

export type {
  TAccess,
};
export {
  useAccess,
};
